import { RootStateInterface } from '@/store/rootState';
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { ListResult, WhatsNewApi } from '../api/whatsNew';
import { WhatsNew } from '../models/WhatsNew';

export interface WhatsNewStateInterface {
  items: WhatsNew[];
  hasMore: boolean;
  currentPage: number;
  recordsPerPage: number;
  hasLoaded: boolean;
  loading: boolean;
  loadingMore: boolean;
}

interface LoadPayload {
  recordsPerPage?: number;
  forceRefresh?: boolean;
}

let firstPageInFlight: Promise<ListResult> | null = null;
let loadMoreInFlight: Promise<ListResult> | null = null;

export const state: WhatsNewStateInterface = {
  items: [],
  hasMore: false,
  currentPage: 1,
  recordsPerPage: 10,
  hasLoaded: false,
  loading: false,
  loadingMore: false,
};

export const mutations: MutationTree<WhatsNewStateInterface> = {
  setLoading(oldState, payload: boolean) {
    oldState.loading = payload;
  },
  setLoadingMore(oldState, payload: boolean) {
    oldState.loadingMore = payload;
  },
  setList(
    oldState,
    payload: {
      items: WhatsNew[];
      hasMore: boolean;
      currentPage: number;
      recordsPerPage: number;
    },
  ) {
    oldState.items = payload.items;
    oldState.hasMore = payload.hasMore;
    oldState.currentPage = payload.currentPage;
    oldState.recordsPerPage = payload.recordsPerPage;
    oldState.hasLoaded = true;
  },
  appendList(oldState, payload: { items: WhatsNew[]; hasMore: boolean; currentPage: number }) {
    const existingIds = new Set(oldState.items.map((item) => item.id));
    const uniqueItems = payload.items.filter((item) => !existingIds.has(item.id));
    oldState.items = [...oldState.items, ...uniqueItems];
    oldState.hasMore = payload.hasMore;
    oldState.currentPage = payload.currentPage;
    oldState.hasLoaded = true;
  },
  reset(oldState) {
    oldState.items = [];
    oldState.hasMore = false;
    oldState.currentPage = 1;
    oldState.hasLoaded = false;
    oldState.loading = false;
    oldState.loadingMore = false;
  },
};

export const getters: GetterTree<WhatsNewStateInterface, RootStateInterface> = {
  unreadCount(st): (lastVisitedAt: number) => number {
    return (lastVisitedAt: number) => {
      return st.items.filter((item) => {
        if (!item.publishedAt) {
          return false;
        }
        return new Date(item.publishedAt).getTime() > lastVisitedAt;
      }).length;
    };
  },
};

export const actions: ActionTree<WhatsNewStateInterface, RootStateInterface> = {
  async preloadList({ dispatch }, payload: LoadPayload = {}) {
    return dispatch('loadFirstPage', payload);
  },

  async loadFirstPage({ state: st, commit }, payload: LoadPayload = {}) {
    const recordsPerPage = payload.recordsPerPage || st.recordsPerPage || 10;
    if (!payload.forceRefresh && st.hasLoaded && st.recordsPerPage === recordsPerPage) {
      return {
        items: st.items,
        hasMore: st.hasMore,
      };
    }
    if (firstPageInFlight) {
      return firstPageInFlight;
    }

    const run = async () => {
      const api = new WhatsNewApi();
      commit('setLoading', true);
      try {
        const result = await api.list(1, recordsPerPage);
        commit('setList', {
          items: result.items,
          hasMore: result.hasMore,
          currentPage: 1,
          recordsPerPage,
        });
        return result;
      } finally {
        commit('setLoading', false);
      }
    };

    firstPageInFlight = run().finally(() => {
      firstPageInFlight = null;
    });
    return firstPageInFlight;
  },

  async loadMore({ state: st, commit }) {
    if (!st.hasMore) {
      return { items: [], hasMore: false };
    }
    if (loadMoreInFlight) {
      return loadMoreInFlight;
    }

    const run = async () => {
      const nextPage = st.currentPage + 1;
      const api = new WhatsNewApi();
      commit('setLoadingMore', true);
      try {
        const result = await api.list(nextPage, st.recordsPerPage);
        commit('appendList', {
          items: result.items,
          hasMore: result.hasMore,
          currentPage: nextPage,
        });
        return result;
      } finally {
        commit('setLoadingMore', false);
      }
    };

    loadMoreInFlight = run().finally(() => {
      loadMoreInFlight = null;
    });
    return loadMoreInFlight;
  },
};

const namespaced = true;

export const whatsNew: Module<WhatsNewStateInterface, RootStateInterface> = {
  namespaced,
  state,
  mutations,
  getters,
  actions,
};
